import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, FloppyDisk, ImageSquare, MagicWand, Scissors, SlidersHorizontal, Trash, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { capacityStatus } from '../../lib/v3ContentCapacity';
import { resolveAssetUrl } from '../../lib/utils';
import ContextHelpButton from '../ContextHelpButton';

const SEMANTIC_FIELDS = [
  ['h1', 'H1'], ['h2', 'H2'], ['h3', 'H3'], ['paragraph', 'Paragraph'], ['cta', 'CTA'],
];
const COVER_READABILITY_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: 'shadow', label: 'Ombra soft' },
  { value: 'dim', label: 'Dim 30%' },
];
const VALID_COVER_READABILITY = new Set(COVER_READABILITY_OPTIONS.map(item => item.value));

function normalizeCoverReadability(value) {
  const normalized = String(value || 'off').trim().toLowerCase();
  return VALID_COVER_READABILITY.has(normalized) ? normalized : 'off';
}

function legacySemantic(slide, index, total) {
  return {
    slide_number: index + 1,
    role: index === 0 ? 'hook' : index === total - 1 ? 'closing' : 'argument',
    h1: index === 0 ? String(slide || '').replace(/^\s*SLIDE\s+\d+\s*:\s*/i, '').trim() : '',
    h2: '', h3: '', paragraph: index === 0 ? '' : String(slide || '').replace(/^\s*SLIDE\s+\d+\s*:\s*/i, '').trim(), cta: '',
    formatting: {},
  };
}

function semanticSlidesFromContent(content) {
  const rawSlides = Array.isArray(content?.slides) ? content.slides : [];
  const semantic = Array.isArray(content?.semantic_slides) ? content.semantic_slides : [];
  const source = semantic.length > 0 ? semantic : rawSlides.map((slide, index) => legacySemantic(slide, index, rawSlides.length));
  return source.map((slide, index) => ({ ...slide, slide_number: index + 1 }));
}

function semanticSlideToLegacy(slide) {
  return ['h3', 'h1', 'h2', 'paragraph', 'cta']
    .map(field => String(slide?.[field] || '').trim())
    .filter(Boolean)
    .join('\n\n');
}

function normalizeSemanticBreaks(value) {
  return String(value || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p\s*>/gi, '\n')
    .replace(/<p(?:\s[^>]*)?>/gi, '')
    .replace(/\r\n?/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function semanticFieldStyle(slide, field) {
  const style = slide?.formatting?.[field] || {};
  return { fontWeight: style.bold ? 700 : undefined, fontStyle: style.italic ? 'italic' : undefined, textDecoration: style.underline ? 'underline' : undefined, whiteSpace: 'pre-wrap' };
}

function slidePosition(asset, total) {
  if (asset?.slide_position === 'last') return total;
  return Math.min(total, Math.max(1, parseInt(asset?.slide_position, 10) || 1));
}

function shiftAssetsAfterInsert(assets, insertedAfter, oldTotal) {
  return (assets || []).map(asset => {
    if (asset?.slide_position === 'last') return asset;
    const position = slidePosition(asset, oldTotal);
    return position > insertedAfter ? { ...asset, slide_position: String(position + 1) } : asset;
  });
}

export default function CarouselTemplateStudioV3({ api, projectId, content, assets = [], disabled = false, allowAiImages = false, onGenerate, onContentChange }) {
  const { t } = useTranslation();
  const [studioSlides, setStudioSlides] = useState(() => semanticSlidesFromContent(content));
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [overrides, setOverrides] = useState({});
  const [selectedSlide, setSelectedSlide] = useState(1);
  const [studioAssets, setStudioAssets] = useState(Array.isArray(assets) ? assets : []);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [savingAssignments, setSavingAssignments] = useState(false);
  const [fitBusy, setFitBusy] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [generateAiImages, setGenerateAiImages] = useState(false);
  const hydratedContentIdRef = useRef(null);

  useEffect(() => {
    setStudioAssets(Array.isArray(assets) ? assets : []);
  }, [assets]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/design/v3/templates`);
        const summaries = data?.templates || [];
        const full = await Promise.all(summaries.map(async summary => {
          try {
            const response = await api.get(`/projects/${projectId}/design/v3/templates/${summary.id}`);
            return { ...summary, ...(response.data || {}) };
          } catch {
            return summary;
          }
        }));
        if (!cancelled) setTemplates(full);
      } catch (error) {
        if (!cancelled) toast.error(`Template V3: ${error.response?.data?.detail || error.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, projectId]);

  useEffect(() => {
    const contentId = content?.id;
    if (!contentId || hydratedContentIdRef.current === contentId) return;
    const persisted = Array.isArray(content?.carousel_template_assignments) ? content.carousel_template_assignments : [];
    setStudioSlides(semanticSlidesFromContent(content));
    setAssignments(Object.fromEntries(persisted.filter(item => item?.slide_number && item?.template_id).map(item => [Number(item.slide_number), item.template_id])));
    setOverrides(Object.fromEntries(persisted.filter(item => item?.slide_number).map(item => [Number(item.slide_number), {
      font_scale: Number(item.font_scale || 1),
      line_height_scale: Number(item.line_height_scale || 1),
      cover_readability: normalizeCoverReadability(item.cover_readability),
    }])));
    setSelectedSlide(1);
    setShowOverride(false);
    hydratedContentIdRef.current = contentId;
  }, [content]);

  useEffect(() => {
    if (selectedSlide > studioSlides.length) setSelectedSlide(Math.max(1, studioSlides.length));
  }, [selectedSlide, studioSlides.length]);

  const templateById = useMemo(() => Object.fromEntries(templates.map(item => [item.id, item])), [templates]);
  const assetBySlide = useMemo(() => Object.fromEntries(studioAssets.map(asset => [slidePosition(asset, studioSlides.length), asset])), [studioAssets, studioSlides.length]);
  const complete = studioSlides.length >= 4 && studioSlides.every(slide => assignments[Number(slide.slide_number)]);
  const selectedAsset = assetBySlide[selectedSlide];
  const selectedSemantic = studioSlides.find(slide => Number(slide.slide_number) === selectedSlide) || null;
  const selectedTemplate = templateById[assignments[selectedSlide]] || null;
  const selectedOverride = useMemo(() => overrides[selectedSlide] || { font_scale: 1, line_height_scale: 1, cover_readability: 'off' }, [overrides, selectedSlide]);
  const selectedFit = useMemo(() => capacityStatus(selectedSemantic, selectedTemplate, selectedOverride), [selectedSemantic, selectedTemplate, selectedOverride]);

  const missingAiPhotoCount = useMemo(() => studioSlides.filter(slide => {
    const number = Number(slide.slide_number);
    const template = templateById[assignments[number]];
    return Boolean(template?.requires_image) && !assetBySlide[number];
  }).length, [studioSlides, templateById, assignments, assetBySlide]);

  const fitFor = (slide, number) => capacityStatus(slide, templateById[assignments[number]], overrides[number] || {});

  const assign = (slideNumber, templateId) => {
    const template = templateById[templateId];
    if (!template) return;
    const asset = assetBySlide[slideNumber];
    if (template.requires_image && !asset) toast.warning(`“${template.name}” richiede una foto: assegnane una alla slide ${slideNumber}.`);
    if (!template.requires_image && asset) toast.warning(`La slide ${slideNumber} ha una foto ma “${template.name}” non contiene uno slot Foto.`);
    setAssignments(prev => ({ ...prev, [slideNumber]: templateId }));
    setSelectedSlide(slideNumber);
  };

  const setLocalOverride = (key, value) => {
    setOverrides(prev => ({ ...prev, [selectedSlide]: { font_scale: 1, line_height_scale: 1, cover_readability: 'off', ...(prev[selectedSlide] || {}), [key]: Number(value) } }));
  };

  const setCoverReadability = value => {
    const normalized = normalizeCoverReadability(value);
    setOverrides(prev => ({
      ...prev,
      [selectedSlide]: {
        font_scale: 1,
        line_height_scale: 1,
        cover_readability: 'off',
        ...(prev[selectedSlide] || {}),
        cover_readability: normalized,
      },
    }));
  };

  const deleteAsset = async asset => {
    if (!asset?.id || disabled || uploadingPhoto) return;
    try {
      await api.delete(`/media/carousel-reference/${content.id}/${asset.id}`);
      setStudioAssets(prev => prev.filter(item => item.id !== asset.id));
      toast.success(`Foto rimossa dalla slide ${selectedSlide}.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossibile rimuovere la foto');
    }
  };

  const uploadPhoto = async file => {
    if (!file || !file.type?.startsWith('image/') || disabled || uploadingPhoto) return;
    setUploadingPhoto(true);
    const tid = toast.loading(`Carico la foto sulla slide ${selectedSlide}…`);
    try {
      const existing = assetBySlide[selectedSlide];
      if (existing?.id) await api.delete(`/media/carousel-reference/${content.id}/${existing.id}`);
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post(`/media/carousel-reference/${content.id}?slide_position=${encodeURIComponent(String(selectedSlide))}`, form);
      setStudioAssets(prev => [...prev.filter(item => slidePosition(item, studioSlides.length) !== selectedSlide), data]);
      toast.success(`Foto assegnata alla slide ${selectedSlide}.`, { id: tid });
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Upload foto non completato', { id: tid });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const assignmentPayload = (slidesArg = studioSlides, assignmentsArg = assignments, overridesArg = overrides) => slidesArg
    .map((slide, index) => {
      const number = Number(slide.slide_number || index + 1);
      const local = overridesArg[number] || {};
      return {
        slide_number: number,
        template_id: assignmentsArg[number],
        font_scale: Number(local.font_scale || 1),
        line_height_scale: Number(local.line_height_scale || 1),
        cover_readability: normalizeCoverReadability(local.cover_readability),
      };
    })
    .filter(item => item.template_id);

  const persistStudioState = async (nextSlides = studioSlides, nextAssignments = assignments, nextOverrides = overrides, nextAssets = studioAssets) => {
    const normalized = nextSlides.map((slide, index) => ({ ...slide, slide_number: index + 1 }));
    const legacySlides = normalized.map(semanticSlideToLegacy);
    const payload = {
      semantic_slides: normalized,
      slides: legacySlides,
      script: legacySlides.join('\n\n-----\n\n'),
      carousel_template_assignments: assignmentPayload(normalized, nextAssignments, nextOverrides),
      carousel_reference_assets: nextAssets,
    };
    await api.put(`/contents/${content.id}`, payload);
    setStudioSlides(normalized);
    setAssignments(nextAssignments);
    setOverrides(nextOverrides);
    setStudioAssets(nextAssets);
    onContentChange?.(normalized, nextAssets, payload.carousel_template_assignments);
    return normalized;
  };

  const saveAssignments = async () => {
    if (!content?.id || disabled || savingAssignments) return;
    setSavingAssignments(true);
    try {
      await persistStudioState();
      toast.success('Carousel V3 salvato');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Impossibile salvare il Carousel V3');
    } finally {
      setSavingAssignments(false);
    }
  };

  const shortenRole = async field => {
    const current = selectedSemantic?.[field];
    const roleFit = selectedFit?.roles?.[field];
    if (!current || !roleFit?.max || fitBusy) return;
    setFitBusy(true);
    const tid = toast.loading(`Accorcio ${field.toUpperCase()}…`);
    try {
      const { data } = await api.post(`/projects/${projectId}/design/v3/content-fit`, {
        project_id: projectId,
        content_id: content.id,
        action: 'shorten',
        field,
        text: current,
        target_words: Math.max(3, roleFit.max),
      });
      const nextSlides = studioSlides.map(slide => Number(slide.slide_number) === selectedSlide ? { ...slide, [field]: data.text } : slide);
      await persistStudioState(nextSlides);
      toast.success(`${field.toUpperCase()} adattato al template`, { id: tid });
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Riduzione testo fallita', { id: tid });
    } finally {
      setFitBusy(false);
    }
  };

  const splitSelected = async () => {
    if (!selectedSemantic?.paragraph || fitBusy) return;
    if (studioSlides.length >= 8) { toast.warning('Il carousel è già a 8 slide: accorcia il testo o usa un override locale.'); return; }
    setFitBusy(true);
    const tid = toast.loading('Divido la slide mantenendo il senso…');
    try {
      const { data } = await api.post(`/projects/${projectId}/design/v3/content-fit`, {
        project_id: projectId,
        content_id: content.id,
        action: 'split',
        field: 'paragraph',
        text: selectedSemantic.paragraph,
        target_words: Math.max(10, selectedFit?.roles?.paragraph?.max || 40),
      });
      const parts = Array.isArray(data?.parts) ? data.parts : [];
      if (parts.length !== 2) throw new Error('Split non valido');
      const oldTotal = studioSlides.length;
      const before = studioSlides.slice(0, selectedSlide - 1);
      const after = studioSlides.slice(selectedSlide);
      const original = studioSlides[selectedSlide - 1];
      const first = { ...original, ...parts[0], cta: '', role: original.role === 'closing' ? 'argument' : original.role };
      const second = { ...original, ...parts[1], cta: original.cta || '', role: original.role === 'closing' ? 'closing' : 'argument' };
      const nextSlides = [...before, first, second, ...after].map((slide, index) => ({ ...slide, slide_number: index + 1 }));
      const nextAssignments = {};
      const nextOverrides = {};
      for (let number = 1; number <= oldTotal; number += 1) {
        const target = number > selectedSlide ? number + 1 : number;
        if (assignments[number]) nextAssignments[target] = assignments[number];
        if (overrides[number]) nextOverrides[target] = overrides[number];
      }
      if (assignments[selectedSlide]) nextAssignments[selectedSlide + 1] = assignments[selectedSlide];
      nextOverrides[selectedSlide + 1] = { ...(overrides[selectedSlide] || { font_scale: 1, line_height_scale: 1, cover_readability: 'off' }) };
      const nextAssets = shiftAssetsAfterInsert(studioAssets, selectedSlide, oldTotal);
      await persistStudioState(nextSlides, nextAssignments, nextOverrides, nextAssets);
      setSelectedSlide(selectedSlide + 1);
      if (selectedTemplate?.requires_image) toast.warning('Il template duplicato richiede una foto anche sulla nuova slide: assegnala prima del render.');
      toast.success('Slide divisa e rinumerata', { id: tid });
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Divisione slide fallita', { id: tid });
    } finally {
      setFitBusy(false);
    }
  };

  const generate = () => {
    if (!complete) { toast.warning('Assegna un Template V3 a tutte le slide.'); return; }
    const mismatch = studioSlides.find(slide => {
      const number = Number(slide.slide_number);
      const template = templateById[assignments[number]];
      const hasAsset = Boolean(assetBySlide[number]);
      if (!template) return true;
      if (template.requires_image && !hasAsset) return !(allowAiImages && generateAiImages);
      return !template.requires_image && hasAsset;
    });
    if (mismatch) {
      const number = Number(mismatch.slide_number);
      const template = templateById[assignments[number]];
      toast.error(template?.requires_image
        ? `Slide ${number}: il template richiede una foto. Caricala oppure attiva la generazione immagini AI.`
        : `Slide ${number}: c’è una foto ma il template scelto non ha uno slot Foto.`);
      return;
    }
    const risky = studioSlides.filter(slide => fitFor(slide, Number(slide.slide_number)).state === 'overflow');
    if (risky.length) toast.warning(`${risky.length} slide hanno ancora un possibile overflow: il renderer farà il fit reale prima dell’export.`);
    onGenerate?.(assignmentPayload(), studioAssets, studioSlides, Boolean(allowAiImages && generateAiImages && missingAiPhotoCount > 0));
  };

  return <div className="space-y-4" data-testid="carousel-template-studio-v3">
    <div className="rounded-xl p-3" style={{ background: 'rgba(168,85,247,.08)', border: '1px solid rgba(168,85,247,.22)' }}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-[var(--accent-purple)]">Carousel Studio V3</p>
        <ContextHelpButton articleId="carousel-studio" label="Guida Carousel Studio" />
      </div>
      <p className="text-[11px] text-[var(--text-muted)] mt-1">Il DNA resta fisso. Qui Sketchario verifica la capacità del template sul contenuto reale e gestisce le eccezioni slide per slide.</p>
    </div>

    <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
      {studioSlides.map((slide, index) => {
        const number = Number(slide.slide_number || index + 1);
        const assigned = templateById[assignments[number]];
        const asset = assetBySlide[number];
        const fit = fitFor(slide, number);
        return <div key={number}
          data-testid={`v3-slide-drop-${number}`}
          onDragOver={event => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }}
          onDrop={event => { event.preventDefault(); assign(number, event.dataTransfer.getData('application/x-sketchario-v3-template')); }}
          onClick={() => setSelectedSlide(number)}
          className={`rounded-xl p-3 transition-all ${selectedSlide === number ? 'ring-1 ring-fuchsia-500' : ''}`}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-xs font-bold" style={{ background: 'rgba(168,85,247,.14)' }}>{number}</div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {SEMANTIC_FIELDS.map(([field,label]) => slide[field] ? <span key={field} className="text-[9px] uppercase tracking-wide text-[var(--accent-purple)]">{label}</span> : null)}
              </div>
              <div className="mt-1 space-y-1">
                {SEMANTIC_FIELDS.map(([field,label]) => slide[field] ? <p key={field} data-testid={`v3-semantic-preview-${number}-${field}`} className={field === 'h1' ? 'text-sm font-semibold' : 'text-xs text-[var(--text-muted)]'} style={semanticFieldStyle(slide,field)}><span className="sr-only">{label}: </span>{normalizeSemanticBreaks(slide[field])}</p> : null)}
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <span className={`text-[10px] px-2 py-1 rounded-full ${assigned ? 'text-emerald-300' : 'text-amber-300'}`} style={{ background: assigned ? 'rgba(16,185,129,.12)' : 'rgba(245,158,11,.12)' }}>
                  {assigned ? `Template: ${assigned.name}` : 'Trascina qui un template'}
                </span>
                {assigned && <span className={`text-[10px] px-2 py-1 rounded-full ${fit.state === 'overflow' ? 'text-amber-300' : 'text-emerald-300'}`} style={{ background: fit.state === 'overflow' ? 'rgba(245,158,11,.12)' : 'rgba(16,185,129,.12)' }} data-testid={`v3-fit-${number}`}>
                  {fit.state === 'overflow' ? `⚠ ${fit.overflow_roles.map(role => role.toUpperCase()).join(', ')}` : '✓ FIT'}
                </span>}
                {asset && <span className="flex items-center gap-1 text-[10px] text-sky-300"><ImageSquare size={11}/>Foto assegnata</span>}
              </div>
            </div>
            {asset && <img src={resolveAssetUrl(asset.url)} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0"/>}
          </div>
        </div>;
      })}
    </div>

    {selectedTemplate && <div className="rounded-xl p-3 space-y-3" style={{ background: selectedFit.state === 'overflow' ? 'rgba(245,158,11,.06)' : 'rgba(16,185,129,.05)', border: `1px solid ${selectedFit.state === 'overflow' ? 'rgba(245,158,11,.3)' : 'rgba(16,185,129,.22)'}` }} data-testid="v3-fit-panel">
      <div className="flex items-start justify-between gap-3">
        <div><p className="text-xs font-semibold">Fit · slide {selectedSlide}</p><p className="text-[10px] text-[var(--text-muted)] mt-1">Capacità stimata per ruolo. Il render finale usa il font reale in Chromium e parte sempre dalla dimensione massima.</p></div>
        <span className={`text-[10px] font-semibold ${selectedFit.state === 'overflow' ? 'text-amber-300' : 'text-emerald-300'}`}>{selectedFit.state === 'overflow' ? 'DA SISTEMARE' : 'FIT'}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {SEMANTIC_FIELDS.map(([field,label]) => {
          const role = selectedFit.roles?.[field];
          if (!selectedSemantic?.[field] || !role?.max) return null;
          return <span key={field} className={`text-[9px] px-2 py-1 rounded-full ${role.state === 'overflow' ? 'text-amber-300 bg-amber-500/10' : 'text-[var(--text-muted)] bg-white/5'}`}>{label}: {role.words}/{role.max}</span>;
        })}
      </div>
      {selectedFit.state === 'overflow' && <div className="flex flex-wrap gap-2">
        {selectedFit.overflow_roles.map(field => <button key={field} type="button" className="btn-ghost text-[10px]" onClick={() => shortenRole(field)} disabled={fitBusy || disabled}><MagicWand size={13}/>Accorcia {field.toUpperCase()}</button>)}
        {selectedFit.overflow_roles.includes('paragraph') && <button type="button" className="btn-ghost text-[10px]" onClick={splitSelected} disabled={fitBusy || disabled || studioSlides.length >= 8}><Scissors size={13}/>Dividi slide</button>}
        <button type="button" className="btn-ghost text-[10px]" onClick={() => setShowOverride(value => !value)}><SlidersHorizontal size={13}/>Regola questa slide</button>
      </div>}
      {(showOverride || Number(selectedOverride.font_scale || 1) !== 1 || Number(selectedOverride.line_height_scale || 1) !== 1) && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-[var(--border-color)]" data-testid="v3-local-override">
        <label className="text-[10px]">Scala font · {Math.round(Number(selectedOverride.font_scale || 1) * 100)}%<input type="range" min="0.65" max="1" step="0.01" value={Number(selectedOverride.font_scale || 1)} onChange={event => setLocalOverride('font_scale', event.target.value)} className="w-full mt-1"/></label>
        <label className="text-[10px]">Interlinea · {Math.round(Number(selectedOverride.line_height_scale || 1) * 100)}%<input type="range" min="0.8" max="1" step="0.01" value={Number(selectedOverride.line_height_scale || 1)} onChange={event => setLocalOverride('line_height_scale', event.target.value)} className="w-full mt-1"/></label>
        <p className="sm:col-span-2 text-[9px] text-[var(--text-muted)]">Override locale: modifica solo questa slide. Il template DNA non cambia.</p>
      </div>}
    </div>}

    <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,.025)', border: '1px solid var(--border-color)' }} data-testid="v3-photo-panel">
      <div className="flex items-center justify-between gap-3">
        <div><p className="text-xs font-semibold">Foto · slide {selectedSlide}</p><p className="text-[10px] text-[var(--text-muted)] mt-1">La foto viene salvata come asset del carousel e resta assegnata a questa slide.</p></div>
        <div className="flex items-center gap-2">
          {selectedAsset && <button type="button" className="btn-ghost px-2 py-1.5" onClick={() => deleteAsset(selectedAsset)} disabled={disabled || uploadingPhoto} title="Rimuovi foto"><Trash size={14}/></button>}
          <label className={`btn-ghost px-3 py-1.5 cursor-pointer ${disabled || uploadingPhoto ? 'opacity-50 pointer-events-none' : ''}`} data-testid="v3-photo-upload-btn"><UploadSimple size={14}/> {selectedAsset ? 'Sostituisci foto' : 'Carica foto'}<input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; uploadPhoto(file); }} /></label>
        </div>
      </div>
      {selectedAsset && <div className="mt-3 flex items-center gap-3"><img src={resolveAssetUrl(selectedAsset.url)} alt="" className="w-16 h-16 rounded-lg object-cover" style={normalizeCoverReadability(selectedOverride.cover_readability) === 'dim' ? { filter: 'brightness(.7)' } : undefined}/><div className="min-w-0"><p className="text-xs truncate">{selectedAsset.original_name || selectedAsset.filename || 'Foto carousel'}</p><p className="text-[10px] text-sky-300 mt-1">Assegnata alla slide {selectedSlide}</p></div></div>}
      {(selectedAsset || selectedTemplate?.requires_image) && <div className="mt-3 pt-3 border-t border-[var(--border-color)]" data-testid="v3-photo-readability">
        <div className="flex items-center justify-between gap-2 mb-2"><p className="text-[10px] font-semibold">Leggibilità testo sulla foto</p><span className="text-[9px] text-[var(--text-muted)]">solo questa slide</span></div>
        <div className="grid grid-cols-3 gap-2">
          {COVER_READABILITY_OPTIONS.map(option => {
            const active = normalizeCoverReadability(selectedOverride.cover_readability) === option.value;
            return <button key={option.value} type="button" className={`btn-ghost min-h-9 justify-center text-[9px] font-semibold ${active ? 'text-fuchsia-200' : ''}`} aria-pressed={active} disabled={disabled} onClick={() => setCoverReadability(option.value)} data-testid={`v3-photo-readability-${option.value}`} style={active ? { borderColor: 'var(--accent-purple)', background: 'rgba(168,85,247,.18)' } : undefined}>{option.label}</button>;
          })}
        </div>
      </div>}
    </div>

    <div>
      <div className="flex items-center justify-between mb-2"><div className="flex items-center gap-2"><p className="text-xs font-semibold">Template V3</p><ContextHelpButton articleId="carousel-templates" label="Guida template Carousel" /></div><p className="text-[10px] text-[var(--text-muted)]">drag → slide</p></div>
      {loading ? <p className="text-xs text-[var(--text-muted)]">Carico template…</p> : <div className="flex gap-2 overflow-x-auto pb-2" data-testid="v3-template-drag-strip">
        {templates.map(template => <button key={template.id} type="button" draggable onDragStart={event => { event.dataTransfer.setData('application/x-sketchario-v3-template', template.id); event.dataTransfer.effectAllowed = 'copy'; }} onClick={() => assign(selectedSlide, template.id)} className="shrink-0 w-[104px] rounded-xl p-2 text-left" style={{ background: 'var(--bg-card)', border: assignments[selectedSlide] === template.id ? '1px solid #d946ef' : '1px solid var(--border-color)' }}>
          <div className="aspect-[4/5] rounded-lg overflow-hidden bg-black/30 relative">{template.thumbnail_url ? <img src={resolveAssetUrl(template.thumbnail_url)} alt="" className="w-full h-full object-cover opacity-70"/> : <div className="absolute inset-2 border border-white/10"><div className="absolute left-2 right-4 bottom-6 h-1 bg-white/50"/><div className="absolute left-2 right-8 bottom-3 h-1 bg-white/30"/></div>}{template.requires_image && <ImageSquare size={14} className="absolute top-2 right-2 text-white"/>}</div>
          <p className="text-[10px] font-semibold truncate mt-2" title={template.name}>{template.name}</p>
          <p className="text-[8px] text-[var(--text-muted)]">{template.requires_image ? 'con foto' : 'senza foto'}</p>
        </button>)}
      </div>}
    </div>

    {allowAiImages && <div className="rounded-xl p-3 flex items-start justify-between gap-4" style={{ background: 'rgba(168,85,247,.07)', border: '1px solid rgba(168,85,247,.22)' }} data-testid="v3-ai-images-option">
      <div className="min-w-0">
        <p className="text-xs font-semibold">{t('editor.v3GenerateAiImages')}</p>
        <p className="text-[10px] text-[var(--text-muted)] mt-1">{t('editor.v3GenerateAiImagesHint', { count: missingAiPhotoCount })}</p>
      </div>
      <button type="button" role="switch" aria-checked={generateAiImages} disabled={disabled || missingAiPhotoCount === 0} onClick={() => setGenerateAiImages(value => !value)} className={`relative w-11 h-6 rounded-full shrink-0 transition-colors ${generateAiImages ? 'bg-fuchsia-500' : 'bg-white/10'} ${missingAiPhotoCount === 0 ? 'opacity-40' : ''}`} data-testid="v3-ai-images-toggle">
        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${generateAiImages ? 'left-6' : 'left-1'}`} />
      </button>
    </div>}

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <button type="button" className="btn-ghost w-full justify-center" disabled={disabled || savingAssignments || Object.keys(assignments).length === 0} onClick={saveAssignments} data-testid="v3-carousel-save-assignments"><FloppyDisk size={14}/>{savingAssignments ? 'Salvo…' : 'Salva carousel'}</button>
      <button type="button" className="btn-gradient w-full justify-center" disabled={disabled || !complete} onClick={generate} data-testid="v3-carousel-generate">{complete ? <><Check size={14}/>Genera con i Template V3</> : `Assegna template (${Object.keys(assignments).length}/${studioSlides.length})`}</button>
    </div>
  </div>;
}